<script lang="ts">
  // PageContainers — `#/containers`.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // node IDs 0:292-440 (PageContainers + ContainerRow 0:329 + bulk bar).
  //
  // Page anatomy:
  //   - Header: title "Containers" + subtitle (count + filters)
  //   - Toolbar: search input + 4 quick-action buttons
  //   - BulkBar: appears when ≥ 1 row is selected, shows selection
  //     count + cap-5 enforcement (surfaces ToastError on overflow)
  //   - Table: 9-column grid — checkbox, name (sortable), image,
  //     state badge, uptime, cpu, mem, actions
  //
  // Spec scenarios covered (R-Containers):
  //   - filters by substring match against name OR image
  //   - NAME header click toggles asc/desc
  //   - sixth selection rejected with ToastError("Bulk select is
  //     limited to 5 containers") — bulk cap = 5
  //   - bulk-action sticky bar mounts when ≥ 1 selected
  //   - row restart button opens ActionModal against that container
  //   - empty-state copy when zero containers exist

  import { onDestroy, onMount } from "svelte";
  import { createWsClient, type WsClient, type SnapshotState } from "../lib/ws";
  import { push } from "../lib/router";
  import ContainerRow from "./ContainerRow.svelte";
  import ActionModal from "./ActionModal.svelte";
  import { formatNumber } from "../lib/format";
  import { bulkMutate, startContainer, stopContainer, restartContainer, BULK_CAP } from "../lib/mutate";
  import type { ContainerRowData, MutateAction } from "../lib/bindings";

  // Snapshot subscription — same pattern as PageOverview / PageHost.
  const isBrowser = typeof window !== "undefined";
  const client: WsClient | null = isBrowser ? createWsClient() : null;
  if (client) client.connect();

  function emptySnapshot(): SnapshotState {
    return {
      host: null,
      containers: [],
      logLines: [],
      connectionState: "disconnected",
      lastError: null,
      lastUpdateMs: null,
      hostHistory: [],
      ramHistory: [],
      cpuHistory: {},
    };
  }

  let snapshot: SnapshotState = client ? initialFrom(client) : emptySnapshot();
  let unsub: (() => void) | null = null;

  function initialFrom(c: WsClient): SnapshotState {
    let v: SnapshotState = emptySnapshot();
    const u = c.snapshot.subscribe((next) => {
      v = next;
    });
    u();
    return v;
  }

  if (client) {
    unsub = client.snapshot.subscribe((next) => {
      snapshot = next;
    });
  }

  onDestroy(() => unsub?.());

  // --- filter / sort / selection state -----------------------------------

  let searchTerm = "";
  // `sortKey` is null until the user clicks a header — the first
  // click then activates asc, the second click toggles to desc.
  // This matches the spec scenario "NAME header click toggles asc/desc".
  let sortKey: "name" | "state" | null = null;
  let sortDir: "asc" | "desc" = "asc";
  let selectedNames: Set<string> = new Set();
  let toastError: string | null = null;

  // Modal state — single instance mounted always so we can toggle open
  // without recreating the dialog (avoids focus-trap flapping).
  let modalOpen = false;
  let modalAction: MutateAction = "restart";
  let modalContainer: string = "";
  let modalBulk: string[] = [];

  // Filter raw containers from the snapshot. The agent fills `image`
  // and `uptime_secs` (container age) from the Docker API; the row
  // shape keeps optional legacy aliases so older snapshots still
  // render with safe defaults.
  $: containers = ((snapshot.containers ?? []) as ContainerRowData[]).map(
    (c) => ({
      ...c,
      image: c.image ?? "",
      uptime_seconds: c.uptime_seconds ?? Number(c.uptime_secs ?? 0),
    }),
  );

  $: filtered = applyFilter(containers, searchTerm);
  $: sorted = applySort(filtered, sortKey, sortDir);

  function applyFilter(
    rows: readonly ContainerRowData[],
    term: string,
  ): ContainerRowData[] {
    const q = term.trim().toLowerCase();
    if (q === "") return [...rows];
    return rows.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.image ?? "").toLowerCase().includes(q),
    );
  }

  function applySort(
    rows: readonly ContainerRowData[],
    key: "name" | "state" | null,
    dir: "asc" | "desc",
  ): ContainerRowData[] {
    if (key === null) return [...rows];
    const out = [...rows];
    out.sort((a, b) => {
      const av = key === "name" ? a.name : a.state;
      const bv = key === "name" ? b.name : b.state;
      const cmp = av.localeCompare(bv);
      return dir === "asc" ? cmp : -cmp;
    });
    return out;
  }

  function toggleSort(key: "name" | "state"): void {
    if (sortKey === key) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortKey = key;
      sortDir = "asc";
    }
  }

  // --- selection logic ---------------------------------------------------

  function toggleOne(name: string, selected: boolean): void {
    if (selected) {
      if (selectedNames.size >= BULK_CAP) {
        toastError = "Bulk select is limited to 5 containers";
        // Clear after a beat so the same error re-arms on the next overflow.
        window.setTimeout(() => (toastError = null), 4000);
        return;
      }
      const next = new Set(selectedNames);
      next.add(name);
      selectedNames = next;
    } else {
      const next = new Set(selectedNames);
      next.delete(name);
      selectedNames = next;
    }
    toastError = null;
  }

  function selectAllVisible(): void {
    const targets = sorted.slice(0, BULK_CAP);
    if (sorted.length > BULK_CAP) {
      toastError = "Bulk select is limited to 5 containers";
      window.setTimeout(() => (toastError = null), 4000);
    }
    selectedNames = new Set(targets.map((c) => c.name));
  }

  function clearSelection(): void {
    selectedNames = new Set();
  }

  function onRowMutate(
    e: CustomEvent<{ action: MutateAction; container: string }>,
  ): void {
    modalAction = e.detail.action;
    modalContainer = e.detail.container;
    modalBulk = [];
    openModal();
  }

  function openModal(): void {
    modalOpen = true;
  }

  function closeModal(): void {
    modalOpen = false;
    modalBulk = [];
  }

  // Stub: when the modal confirms, the parent (PageContainers) would
  // dispatch the actual mutate call. For PR 3 the mutate wiring lives
  // in lib/mutate.ts; we surface success/failure via the toast +
  // refresh logic in a follow-up if needed. The modal contract is the
  // unit being tested here; PR 7 wires the toast success.
  function onModalConfirm(
    e: CustomEvent<Record<string, never>>,
  ): void {
    void e;
    // Bulk path: one POST /api/bulk for the whole selection.
    if (modalBulk.length > 0) {
      bulkMutate({
        action: modalAction,
        containers: modalBulk,
      }).then(() => {
        selectedNames = new Set();
        closeModal();
      }).catch((err: Error) => {
        toastError = err.message;
        window.setTimeout(() => (toastError = null), 4000);
      });
      return;
    }
    // Single container path — dispatch the real mutate call.
    const call = { action: modalAction, container: modalContainer };
    const fn =
      modalAction === "start"
        ? startContainer
        : modalAction === "stop"
          ? stopContainer
          : restartContainer;
    fn(call)
      .then(() => closeModal())
      .catch((err: Error) => {
        toastError = err.message;
        window.setTimeout(() => (toastError = null), 4000);
      });
  }

  function onModalCancel(): void {
    closeModal();
  }

  // Bulk-bar shortcuts.
  function bulk(action: MutateAction): void {
    if (selectedNames.size === 0) return;
    if (selectedNames.size > BULK_CAP) {
      toastError = "Bulk select is limited to 5 containers";
      return;
    }
    modalAction = action;
    modalContainer = "";
    modalBulk = [...selectedNames];
    openModal();
  }

  function backToOverview(): void {
    push("/");
  }

  onMount(() => {
    // No-op for now; kept for parity with future pages that need an
    // explicit mount hook (e.g. focus management).
    return () => {};
  });
</script>

<section class="page-containers" aria-labelledby="containers-heading">
  <header class="page-containers__head">
    <div class="page-containers__title-block">
      <button
        type="button"
        class="page-containers__back"
        data-testid="containers-back"
        aria-label="Back to overview"
        on:click={backToOverview}
      >←</button>
      <h2 id="containers-heading" class="page-containers__title">Containers</h2>
    </div>
    <span class="page-containers__subtitle" data-testid="containers-count">
      {formatNumber(sorted.length)} of {formatNumber(containers.length)} shown
    </span>
  </header>

  <div class="page-containers__toolbar">
    <input
      type="search"
      class="search-input"
      data-testid="containers-search"
      placeholder="Filter by name or image"
      aria-label="Filter containers"
      bind:value={searchTerm}
    />
    <div class="page-containers__quick-actions">
      <button
        type="button"
        class="action-btn"
        data-testid="select-all"
        on:click={selectAllVisible}
        aria-label="Select all visible"
      >Select all</button>
      <button
        type="button"
        class="action-btn"
        data-testid="bulk-start"
        disabled={selectedNames.size === 0}
        on:click={() => bulk("start")}
      >Start all</button>
      <button
        type="button"
        class="action-btn"
        data-testid="bulk-stop"
        disabled={selectedNames.size === 0}
        on:click={() => bulk("stop")}
      >Stop all</button>
      <button
        type="button"
        class="action-btn"
        data-testid="bulk-restart"
        disabled={selectedNames.size === 0}
        on:click={() => bulk("restart")}
      >Restart all</button>
    </div>
  </div>

  {#if toastError}
    <div
      class="page-containers__toast page-containers__toast--err"
      role="alert"
      data-testid="bulk-toast-error"
    >{toastError}</div>
  {/if}

  {#if selectedNames.size > 0}
    <div class="page-containers__bulk-bar" data-testid="bulk-bar">
      <span data-testid="bulk-count">{selectedNames.size} selected</span>
      <button
        type="button"
        class="action-btn action-btn--ghost"
        on:click={clearSelection}
      >Clear</button>
    </div>
  {/if}

  <div class="page-containers__table" role="table" aria-label="Containers">
    <div class="page-containers__row page-containers__row--head" role="row">
      <span class="page-containers__cell page-containers__cell--checkbox" role="columnheader" aria-label="Select"></span>
      <button
        type="button"
        class="page-containers__cell page-containers__cell--name page-containers__sort-btn"
        role="columnheader"
        data-testid="sort-name"
        on:click={() => toggleSort("name")}
      >
        NAME
        <span class="page-containers__sort-arrow" aria-hidden="true">
          {sortKey === "name" ? (sortDir === "asc" ? "↑" : "↓") : ""}
        </span>
      </button>
      <span class="page-containers__cell page-containers__cell--image" role="columnheader">IMAGE</span>
      <button
        type="button"
        class="page-containers__cell page-containers__cell--state page-containers__sort-btn"
        role="columnheader"
        data-testid="sort-state"
        on:click={() => toggleSort("state")}
      >
        STATE
        <span class="page-containers__sort-arrow" aria-hidden="true">
          {sortKey === "state" ? (sortDir === "asc" ? "↑" : "↓") : ""}
        </span>
      </button>
      <span class="page-containers__cell page-containers__cell--uptime" role="columnheader">UPTIME</span>
      <span class="page-containers__cell page-containers__cell--cpu" role="columnheader">CPU</span>
      <span class="page-containers__cell page-containers__cell--mem" role="columnheader">MEM</span>
      <span class="page-containers__cell page-containers__cell--actions" role="columnheader">ACTIONS</span>
    </div>

    {#if sorted.length === 0}
      <div class="page-containers__empty" data-testid="containers-empty">
        <p class="page-containers__empty-title">No containers</p>
        <p class="page-containers__empty-detail">
          {containers.length === 0
            ? "No containers reported by the agent yet."
            : "No rows match your filter."}
        </p>
      </div>
    {:else}
      {#each sorted as c (c.id)}
        <ContainerRow
          container={c}
          selected={selectedNames.has(c.name)}
          on:mutate={onRowMutate}
          on:toggle={(e) => toggleOne(e.detail.container, e.detail.selected)}
        />
      {/each}
    {/if}
  </div>

  <ActionModal
    open={modalOpen}
    action={modalAction}
    container={modalContainer}
    bulkTargets={modalBulk}
    on:confirm={onModalConfirm}
    on:cancel={onModalCancel}
  />
</section>

<style>
  .page-containers {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .page-containers__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .page-containers__title-block {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .page-containers__back {
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-mid);
    width: 32px;
    height: 32px;
    cursor: pointer;
  }
  .page-containers__back:hover {
    color: var(--text-strong);
    background: var(--surface-3);
  }

  .page-containers__title {
    font: var(--type-h1);
    color: var(--text-strong);
  }

  .page-containers__subtitle {
    font: var(--type-mono-md);
    color: var(--text-mid);
  }

  .page-containers__toolbar {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  .search-input {
    flex: 1;
    min-width: 240px;
    height: 36px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-strong);
    padding: 0 var(--space-3);
    font: var(--type-body);
  }
  .search-input::placeholder {
    color: var(--text-mid);
  }

  .page-containers__quick-actions {
    display: inline-flex;
    gap: var(--space-2);
  }

  .action-btn {
    height: 32px;
    padding: 0 var(--space-4);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-strong);
    font: var(--type-body-sm);
    font-weight: 500;
    cursor: pointer;
    transition:
      background var(--motion-fast) var(--easing-standard),
      color var(--motion-fast) var(--easing-standard);
  }
  .action-btn:hover:not(:disabled) {
    background: var(--surface-3);
  }
  .action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .action-btn--ghost {
    background: transparent;
    color: var(--text-mid);
  }

  .page-containers__toast {
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    font: var(--type-body-sm);
    border: 1px solid var(--border);
  }
  .page-containers__toast--err {
    color: var(--err-red);
    background: var(--err-bg);
    border-color: rgba(239, 68, 68, 0.40);
  }

  .page-containers__bulk-bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--accent-bg);
    border: 1px solid rgba(79, 70, 229, 0.30);
    border-radius: var(--radius-sm);
    color: var(--text-strong);
    font: var(--type-body-sm);
    font-weight: 500;
    position: sticky;
    top: 0;
    z-index: 4;
  }

  .page-containers__table {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .page-containers__row {
    display: grid;
    grid-template-columns: 32px 200px 1fr 120px 96px 72px 96px 144px;
    align-items: center;
    gap: var(--space-3);
    padding: 0 var(--space-4);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }

  .page-containers__row--head {
    height: 36px;
    background: var(--surface-2);
    color: var(--text-dim);
    font: var(--type-label-caps);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 11px;
  }

  .page-containers__cell {
    color: inherit;
    font: inherit;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 0;
  }

  .page-containers__sort-btn {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }
  .page-containers__sort-btn:hover {
    color: var(--text-strong);
  }

  .page-containers__sort-arrow {
    color: var(--accent-cyan);
    font-weight: 700;
  }

  .page-containers__empty {
    padding: var(--space-6);
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
    text-align: center;
  }
  .page-containers__empty-title {
    margin: 0;
    font: var(--type-h2);
    color: var(--text-strong);
  }
  .page-containers__empty-detail {
    margin: var(--space-2) 0 0;
    color: var(--text-mid);
    font: var(--type-body);
  }
</style>